// API client for Assessment test series (/api/assessment/admin/test-series).
//
// Envelope: { data, isError, statusCode, errorMessage }. The list is a page
// { content, page, size, totalElements, totalPages, hasNext } whose rows are the
// raw series entity { id, courseId, name, description, accessType, price, durationDays, status, createdBy,
// updatedBy, createdAt, updatedAt, version } — the admin list does NOT carry
// test/student counts (those exist only on the student-facing endpoints).
// GET /{id} returns { series, tests: [{ testId, testName, displayOrder, testTypeId }] }.
//
// Lifecycle: a series is created as DRAFT. Only DRAFT series can be edited,
// have their member tests changed/reordered, or be published (publishing needs
// at least one member test). Only PUBLISHED tests of the SAME course can be
// added, and a test can only be added once. DELETE hard-deletes a DRAFT series
// (and its memberships) and archives (soft) any other. A series' course is
// immutable: update only changes name and description.
import api from "../axiosInstance";

const BASE = "/api/assessment/admin/test-series";

export { apiErrorMessage } from "./assessmentHierarchy";

const unwrap = (res) => res.data?.data;

export const SERIES_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED"];

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

// filters: courseId, status. Returns the page envelope.
export const listTestSeries = async ({ page = 0, size = 20, ...filters } = {}) => {
  const res = await api.get(BASE, { params: cleanParams({ ...filters, page, size }) });
  const data = unwrap(res) || {};
  return { ...data, content: data.content || [] };
};

// -> { series, tests: [{ testId, testName, displayOrder, testTypeId }] }
export const getTestSeriesDetail = (id) => api.get(`${BASE}/${id}`).then(unwrap);

// Payload: { courseId (create only), name, description, accessType, price, durationDays }.
// accessType is FREE or PREMIUM; PREMIUM needs price > 0 and durationDays > 0. On update, leaving
// accessType out keeps the series' current access settings.
export const createTestSeries = (payload) => api.post(BASE, payload).then(unwrap);
export const updateTestSeries = (id, payload) => api.put(`${BASE}/${id}`, payload).then(unwrap);
export const deleteTestSeries = (id) => api.delete(`${BASE}/${id}`).then(unwrap);
export const publishTestSeries = (id) => api.post(`${BASE}/${id}/publish`).then(unwrap);

// --- Member tests (DRAFT series only) ---------------------------------------
export const addTestToSeries = (seriesId, testId) =>
  api.post(`${BASE}/${seriesId}/tests`, { testId }).then(unwrap);
export const removeTestFromSeries = (seriesId, testId) =>
  api.delete(`${BASE}/${seriesId}/tests/${testId}`).then(unwrap);
// testIds must be exactly the series' current member test ids, in the new order.
export const reorderSeriesTests = (seriesId, testIds) =>
  api.put(`${BASE}/${seriesId}/tests/reorder`, { testIds }).then(unwrap);
